import { trackerEvents } from "../constants.js";
import { trackerStore, device } from "./Store.js";
import { Session } from "./Session.js";
import { Report } from "./Report.js";
import { getFilename, fetchRetry } from "./trackerUtilities.js";
import { VERSION } from "../version.js";

/** Seconds between heartbeats when the embedder does not say. */
export const DEFAULT_HEARTBEAT_RATE = 10;
/**
 * Browsers cap the bodies of in-flight keepalive requests at 64 KiB in
 * total; larger unload posts go out as normal requests instead.
 */
export const KEEPALIVE_LIMIT = 60 * 1024;
/** Extra attempts for posts that are not made while the page goes away. */
export const RETRY_ATTEMPTS = 3;
const RETRY_BACKOFF_MS = 250;

function heartbeatRate(value) {
	const rate = Number(value);
	return Number.isFinite(rate) && rate > 0 ? rate : DEFAULT_HEARTBEAT_RATE;
}

export class Tracker {
	static DATA_COLLECTION = "data-collection";

	constructor(config) {
		this.store = trackerStore;
		this.post_url = config.post_url;
		this.epub_id = config.epub_id;
		// Rails did not send heartbeat_rate at first; undefined * 1000 made
		// every heartbeat timer fire immediately.
		this.heartbeat_rate = heartbeatRate(config.heartbeat_rate);
		this.reader_type = config.reader_type || "cassis-sample";
		this.version = config.version || "1.0";
		this.deviceId = device.id;
		this.tracks = [];
		this.locale = "en";
		this.bookplateButton = config.bookplateButton || "no-tracking-button";
		this.buyButton = config.buyButton || "jb-buy";
		this.app = "Cassis Reader";
		this.appVersion = VERSION;
		// Whether anything was recorded since the last post: a stop that
		// follows a pause post with nothing new in between is skipped.
		this.recordedSincePost = false;
	}

	/** True while a session is recording. */
	get active() {
		return Boolean(this.session?.heartbeat);
	}

	save(key, value) {
		this.store.setKeySync(this.epub_id + key, JSON.stringify(value));
	}

	read(key) {
		return JSON.parse(this.store.getKeySync(this.epub_id + key));
	}

	/** Inits the Tracker (deviceId + tracks) and dispatches a trackerLoaded custom event */
	initTracking(spineItems, context, locale, aboutBuyButton) {
		this.bookElements = context;
		if (locale) {
			this.locale = locale;
		}
		this.createTracks(spineItems);

		this.popEvent("trackerLoaded", { deviceId: this.deviceId });

		if (aboutBuyButton) {
			aboutBuyButton.href = this.appendDeviceId(aboutBuyButton.href, true);
		}
	}

	/** Creates a deviceId and starts a new session and its recording */
	startTracking(url) {
		this.track = getFilename(url);
		this.startChapterSession();
	}

	startChapterSession() {
		if (this.shouldBeTracked(this.track, this.tracks)) {
			this.session = new Session(this.track, this.heartbeat_rate, this.epub_id);
			this.session.startSession(trackerEvents.Started);
			this.recordedSincePost = true;
		}
	}

	/**
	 * Stops the current session and posts what has been recorded. Stops
	 * and pauses happen as the page is hidden or unloaded, so the post uses
	 * keepalive (when small enough) and is not retried. Returns the post's
	 * promise, which never rejects.
	 */
	stopTracking(trackerState = trackerEvents.Stopped) {
		if (!this.session) return Promise.resolve();
		if (!this.active && !this.recordedSincePost) {
			// Already stopped (a pause post went out) and nothing since.
			return Promise.resolve();
		}
		try {
			this.session.unload(trackerState);
			/* c8 ignore next 3 */
		} catch (err) {
			console.error(err);
		}
		return this.sync({ unload: true });
	}

	resumeTracking(trackerState = trackerEvents.Resumed) {
		// Resuming twice (visibilitychange and a bfcache pageshow can both
		// fire) must not start a second session.
		if (this.session?.id === this.track && !this.active) {
			this.session = new Session(this.track, this.heartbeat_rate, this.epub_id);
			this.session.startSession(trackerState);
			this.recordedSincePost = true;
		}
	}

	pauseTracking(trackerState = trackerEvents.Paused) {
		if (!this.active) return Promise.resolve();
		return this.stopTracking(trackerState);
	}

	/** Handles user interactions in the navigator and viewer */
	handleEvents(element, xpath) {
		this.noteRecording();
		this.session?.click(element, xpath);
	}

	/** Handle bookmarks creator and deletion */
	handleAnnotations(type, uuid) {
		this.noteRecording();
		this.session?.annotation(type, uuid);
	}

	/** Handles in-chapter progression */
	handleProgression(type, progression, spread, duration, page, pageCount) {
		this.noteRecording();
		this.session?.progress(
			type,
			progression,
			spread,
			duration,
			page,
			pageCount,
		);
	}

	/** Handles in-chapter preference changes */
	handlePreference(type, value) {
		this.noteRecording();
		this.session?.preference(type, value);
	}

	/** Handles in-chapter shares */
	handleShare(type, nonce, where) {
		this.noteRecording();
		this.session?.share(type, nonce, where);

		// For this one we want to get nonce ASAP so we POST prematurely
		this.makeSyncNow();
	}

	/** Make a payload and POST immediately when called, with retries */
	makeSyncNow() {
		return this.sync({ unload: false });
	}

	noteRecording() {
		// Events are only stored while a session is recording.
		if (this.active) this.recordedSincePost = true;
	}

	/**
	 * Posts every stored session that is due and, once the endpoint has
	 * accepted them, removes the ended ones from storage.
	 */
	sync({ unload }) {
		let payload;
		try {
			payload = this.createPayload();
			/* c8 ignore next 4 */
		} catch (err) {
			console.error(err);
			return Promise.resolve();
		}
		this.recordedSincePost = false;
		if (payload === null) return Promise.resolve();
		const report = this.report;
		return this.postPayload(payload, { unload }).then(
			() => report.removeEnded(),
			(err) => {
				// Not delivered: the records stay stored for the next post.
				this.recordedSincePost = true;
				console.error(err);
			},
		);
	}

	/** Creates an array of chapters to record and sync */
	createTracks(tracks) {
		// Don’t rely on localStorage given the readingOrder can change for gated samples
		this.tracks = tracks
			.map((item) => {
				if (item.href) {
					return getFilename(item.href);
				}
			})
			.filter((item) => item);
		return this.tracks;
	}

	/** Updates the array of chapters to record and sync */
	updateTracks(tracks) {
		this.tracks = [];
		return this.createTracks(tracks);
	}

	getTracks() {
		return this.tracks;
	}

	/** Creates and dispatches a custom event */
	popEvent(name, data) {
		const evt = new CustomEvent(name, {
			bubbles: true,
			detail: data,
		});
		document.dispatchEvent(evt);
	}

	/** Appends the deviceId at the end of an existing href, but before an optional fragment/hash */
	appendDeviceId(href, sameOrigin = false) {
		const url = new URL(href);
		if (sameOrigin) {
			const isSameOrigin =
				window.location.protocol === url.protocol &&
				window.location.port === url.port &&
				window.location.hostname === url.hostname;
			if (!isSameOrigin) {
				return href;
			}
		}
		url.searchParams.append("s", this.deviceId);
		return url.href;
	}

	/** Checks if the current location should be tracked */
	shouldBeTracked(track, tracks) {
		return tracks.indexOf(track) >= 0;
	}

	/** Creates a payload, or null when there is nothing to send */
	createPayload() {
		this.report = new Report(
			this.tracks,
			this.epub_id,
			this.version,
			this.track,
			new Date().toISOString(),
			this.heartbeat_rate,
			this.app,
			this.appVersion,
			this.deviceId,
			this.reader_type,
		);
		this.payload = this.report.wrapStream();
		return this.payload;
	}

	/** Posts the payload */
	postPayload(stream, { unload = false } = {}) {
		const body = JSON.stringify(stream);
		const options = {
			method: "POST",
			body,
			mode: "cors",
			headers: {
				"Content-Type": "application/json",
			},
		};
		if (unload) {
			// keepalive lets the request outlive the page; there is no time
			// to retry.
			if (new TextEncoder().encode(body).length < KEEPALIVE_LIMIT) {
				options.keepalive = true;
			}
			return fetchRetry(this.post_url, options, 0);
		}
		return fetchRetry(this.post_url, options, RETRY_ATTEMPTS, RETRY_BACKOFF_MS);
	}
}
